import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../../authorization";
import type { Schema } from "../../generatedDrizzleSchema";
import { zql } from "../../generatedDrizzleSchema";

type Tx = Pick<Transaction<Schema>, "run">;

export type IdpOwnedConcern =
  | "userProvisioning"
  | "userActivation"
  | "groupMembership"
  | "groupLifecycle";

const LOCKING_SYNC_MODES: Record<IdpOwnedConcern, ReadonlySet<string>> = {
  userProvisioning: new Set(["jit", "jit_users_and_groups", "scim"]),
  userActivation: new Set(["scim"]),
  groupMembership: new Set(["jit_users_and_groups", "scim"]),
  groupLifecycle: new Set(["scim"]),
};

function syncModeLocksConcern(
  syncMode: string | null,
  concern: IdpOwnedConcern
): boolean {
  return syncMode !== null && LOCKING_SYNC_MODES[concern].has(syncMode);
}

// The single predicate both the server guard (assertIdpSyncNotActive) and the UI
// hook (useIdpSyncLock) use to decide a concern is locked: returns the syncMode
// of the first provider that owns it, or null if none do.
export function findLockingSyncMode(
  providers: ReadonlyArray<{ syncMode: string | null }>,
  concern: IdpOwnedConcern
): string | null {
  return (
    providers.find((p) => syncModeLocksConcern(p.syncMode, concern))
      ?.syncMode ?? null
  );
}

export async function assertIdpSyncNotActive(
  tx: Tx,
  tenantId: string,
  concern: IdpOwnedConcern
): Promise<void> {
  const providers = await tx.run(zql.ssoProvider.where("tenantId", tenantId));
  if (findLockingSyncMode(providers, concern) !== null) {
    throw userFacingError(
      "RBAC changes are locked while IdP sync is active. Disable sync to make changes."
    );
  }
}
